import { useState } from "react";
import { Checkbox, Input, Modal, Select, Space, Text, useToast } from "@app/ui";
import { createOrgSelfSchema } from "@app/core/schemas";
import { createOrg, createOrgSelf, createSubOrg } from "../api/dpp.js";
import { useAuth } from "../auth/AuthContext.js";
import { useWebConfig } from "../config/useWebConfig.js";
import { useT } from "../i18n.js";
import { ErrorAlert, useFormErrors } from "../errors/index.js";
import { useOrgs } from "./OrgContext.js";
import type { BillingPlan } from "../types.js";

/** Mirrors @app/core BILLING_PLANS; labels are localized in the modal. */
const PLANS: readonly BillingPlan[] = ["free", "unlimited", "none"];

/**
 * Whether the current user can provision a *top-level* org — either a platform
 * admin (global `org:manage`, provisions via `/admin/orgs`) or, on a SaaS-style
 * deployment, any user via the open self-service endpoint. Shared by the header
 * switcher and the no-org empty state so both offer the CTA under the same rule.
 */
export function useCanCreateOrg(): boolean {
  const { hasPermission } = useAuth();
  const { orgSelfServiceEnabled } = useWebConfig();
  return hasPermission("org:manage") || orgSelfServiceEnabled;
}

/**
 * The "new organization" form, extracted so both `OrgSwitcher` (header "+") and
 * `NoOrgState` (empty-state CTA) can open the exact same flow. Controlled via
 * `open`/`onClose`; on success it refreshes the org list and selects the new org.
 */
export function CreateOrgModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT();
  const toast = useToast();
  const { user, hasPermission } = useAuth();
  const { org, refreshOrgs } = useOrgs();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [gs1Prefix, setGs1Prefix] = useState("");
  const [plan, setPlan] = useState<BillingPlan>("free");
  const [selfOwner, setSelfOwner] = useState(true);
  const [asSubOrg, setAsSubOrg] = useState(false);
  const [saving, setSaving] = useState(false);
  const errs = useFormErrors();

  const canManage = hasPermission("org:manage");
  // Sub-orgs can only be opened by an admin of the currently selected org.
  const canCreateSubOrg = org?.memberRole === "admin";

  const close = () => {
    setName("");
    setSlug("");
    setGs1Prefix("");
    setAsSubOrg(false);
    errs.reset();
    onClose();
  };

  const submit = async () => {
    // Validate the shared profile fields BEFORE any request — the same schema the
    // API enforces, so what fails here is exactly what would fail server-side.
    const parsed = errs.validate(createOrgSelfSchema, {
      name: name.trim(),
      slug: slug.trim(),
      ...(gs1Prefix.trim() ? { gs1CompanyPrefix: gs1Prefix.trim() } : {}),
    });
    if (!parsed.ok) return;

    setSaving(true);
    try {
      const base = parsed.data;
      // Sub-org under the selected org (creator becomes its admin); else platform
      // admins provision via /admin/orgs, everyone else self-serves via /orgs.
      const created =
        asSubOrg && org
          ? await createSubOrg(org.id, base)
          : canManage
            ? await createOrg({
                ...base,
                billingPlan: plan,
                ...(selfOwner && user ? { ownerUserId: user.id } : {}),
              })
            : await createOrgSelf(base);
      toast.success(t("org.created"));
      close();
      // Sub-org + self-service both enroll the creator as admin; the platform
      // admin path only enrolls when the "add me as owner" box is ticked.
      await refreshOrgs(asSubOrg || !canManage || selfOwner ? created.id : undefined);
    } catch (e) {
      errs.handle(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title={t("org.createTitle")}
      okText={t("common.create")}
      cancelText={t("common.cancel")}
      onOk={() => void submit()}
      confirmLoading={saving}
    >
      <Space direction="vertical" size="md" style={{ width: "100%" }}>
        {errs.formError && <ErrorAlert error={errs.formError} onRetry={() => void submit()} />}
        <Input
          name="name"
          label={t("org.name")}
          required
          value={name}
          error={errs.fieldErrors.name}
          onChange={(e) => {
            setName(e.target.value);
            errs.clearField("name");
          }}
          autoFocus
        />
        <Input
          name="slug"
          label={t("org.slug")}
          required
          help={t("org.slugHelp")}
          value={slug}
          error={errs.fieldErrors.slug}
          onChange={(e) => {
            setSlug(e.target.value);
            errs.clearField("slug");
          }}
        />
        <Input
          name="gs1CompanyPrefix"
          label={t("org.gs1Prefix")}
          help={t("org.gs1PrefixHelp")}
          value={gs1Prefix}
          error={errs.fieldErrors.gs1CompanyPrefix}
          onChange={(e) => {
            setGs1Prefix(e.target.value);
            errs.clearField("gs1CompanyPrefix");
          }}
        />
        {canCreateSubOrg && org && (
          <Checkbox checked={asSubOrg} onChange={(e) => setAsSubOrg(e.target.checked)}>
            <Text small>
              {t("org.asSubOrg")} {org.name}
            </Text>
          </Checkbox>
        )}
        {canManage && !asSubOrg && (
          <>
            <Select
              label={t("org.plan")}
              options={PLANS.map((p) => ({ label: t(`billingPlan.${p}`), value: p }))}
              value={plan}
              onChange={(v) => setPlan(v as BillingPlan)}
            />
            <Checkbox checked={selfOwner} onChange={(e) => setSelfOwner(e.target.checked)}>
              <Text small>{t("org.addSelfOwner")}</Text>
            </Checkbox>
          </>
        )}
      </Space>
    </Modal>
  );
}
